// The one place Go to Definition navigations actually happen.
//
// Monaco routes every real "open this location" through
// `ICodeEditorService.openCodeEditor`: F12 / context-menu Go to Definition and
// Ctrl/Cmd+click (`SymbolNavigationAction._openReference`), and opening a row
// of the Peek Definition widget (`ReferencesController._gotoReference` /
// `openReference`). The Ctrl/Cmd+hover link probe never does.
// `monaco.editor.registerEditorOpener` hooks that call ahead of Monaco's own
// handler (handlers are unshifted), so:
//
//  - a `jamsql-nav:` location (table/view, column, routine, overloads) runs its
//    navigation here — once per real navigation;
//  - an in-buffer location (alias, CTE) is only COUNTED here, and Monaco moves
//    the cursor inside the same model as before. The count also runs from the
//    Peek References opener (`handlePeekOpen`), which claims `jamsql-ref:`
//    opens — an alias jump inside a stored-object preview — and may run
//    before this one: opener order is registration order, so counting must
//    not depend on it. A matched answer is consumed, so the two paths never
//    count one open twice.
//
// Both return `false`, deliberately, also after a `jamsql-nav:` navigation ran:
// nothing was opened in an EDITOR, and Monaco's follow-up for a claimed open
// acts on the editor the opener hands back — the source editor. After a peek
// row open (`_gotoReference`) it would re-show the peek at the synthetic
// target range (line 1 of the description), scrolling the source editor to
// line 1; after F12 it would flash that range in the source. With `false`
// (no other opener claims the scheme, and Monaco's default handler has no
// editor showing it) `openCodeEditor` resolves `null`: `_gotoReference` closes
// the peek and leaves the source cursor and viewport where they were, and
// `_openReference` stops.
//
// `feature:navfeatures_goto_definition` fires from here, never from the
// provider, so the event counts navigations rather than hover probes.
//
// The navigation runs synchronously (unlike `handlePeekOpen`, which defers):
// none of these side effects dispose the source editor — workspace tabs stay
// mounted while hidden, and the OE reveal / picker are plain store writes.
//
// Installed once per renderer (idempotent latch), like the peek-reference
// integration; the handle is never disposed by an editor unmount.

import type * as monacoNs from 'monaco-editor';

import { trackNavFeature } from '../analytics/trackNavFeature';
import { navigateToColumn } from './columnNavigation';
import { lookupDefinitionNavTarget } from './definitionNavRegistry';
import type { DefinitionNavTarget } from './definitionNavTarget';
import { isDefinitionNavScheme } from './definitionNavTarget';
import { navigateToObjectExplorer } from './externalNavigation';
import { navigateToRoutine } from './routineNavigation';
import { useRoutinePickerStore } from './routinePickerStore';

/** Structural subset of the Monaco namespace the opener needs. Optional so a
 *  partial Monaco stub (unit tests, embedded hosts) cannot break the editor. */
export type DefinitionOpenerMonacoApi = {
  readonly editor: {
    registerEditorOpener?: (opener: monacoNs.editor.ICodeEditorOpener) => monacoNs.IDisposable;
  };
};

type InBufferVia = 'alias-decl' | 'cte-body';

type InBufferDefinition = {
  readonly lineNumber: number;
  readonly column: number;
  readonly via: InBufferVia;
};

/** Recent alias / CTE answers remembered per model, and models remembered. */
export const MAX_IN_BUFFER_ANSWERS_PER_MODEL = 16;
const MAX_MODELS_WITH_ANSWERS = 64;

let installed: monacoNs.IDisposable | null = null;

/** Recent in-buffer (alias / CTE) answers per model URI, newest last, keyed by
 *  the target's start. No timeout and nothing is dropped when other answers
 *  arrive — only a matching open consumes its entry — so a Peek Definition
 *  left open while the user hovers elsewhere still counts when its row is
 *  opened. Residual undercount, accepted: a row opened after more than
 *  MAX_IN_BUFFER_ANSWERS_PER_MODEL other alias / CTE answers in that model. */
const inBufferByModel = new Map<string, InBufferDefinition[]>();

/** Idempotent install; a second call returns the live handle. */
export function installDefinitionNavigationOpener(
  monaco: DefinitionOpenerMonacoApi,
): monacoNs.IDisposable {
  if (installed) return installed;
  if (typeof monaco.editor.registerEditorOpener !== 'function') {
    return { dispose: () => undefined };
  }
  const hook = monaco.editor.registerEditorOpener({
    openCodeEditor: (_source, resource, selectionOrPosition) =>
      handleDefinitionOpen(resource, selectionOrPosition),
  });
  installed = {
    dispose: () => {
      hook.dispose();
      installed = null;
    },
  };
  return installed;
}

/** Test-only — drop the install latch and every pending in-buffer record. */
export function __resetDefinitionNavigationOpenerForTest(): void {
  installed?.dispose();
  installed = null;
  inBufferByModel.clear();
}

/** Called by the DefinitionProvider for every alias / CTE answer (probe or
 *  not). Counted only if Monaco then opens exactly that spot. */
export function noteInBufferDefinition(args: {
  readonly uri: string;
  readonly range: monacoNs.IRange;
  readonly via: InBufferVia;
}): void {
  const answer = { lineNumber: args.range.startLineNumber, column: args.range.startColumn, via: args.via };
  const answers = (inBufferByModel.get(args.uri) ?? []).filter(
    (a) => a.lineNumber !== answer.lineNumber || a.column !== answer.column,
  );
  answers.push(answer);
  if (answers.length > MAX_IN_BUFFER_ANSWERS_PER_MODEL) answers.shift();
  inBufferByModel.delete(args.uri); // re-insert: the map's order is recency
  inBufferByModel.set(args.uri, answers);
  if (inBufferByModel.size > MAX_MODELS_WITH_ANSWERS) {
    const oldest = inBufferByModel.keys().next().value;
    if (oldest !== undefined) inBufferByModel.delete(oldest);
  }
}

/** Count an open that lands exactly on a remembered alias / CTE answer, and
 *  consume it. Safe to call from every opener that sees the open. */
export function countInBufferDefinitionOpen(
  resource: { toString(): string },
  selectionOrPosition?: monacoNs.IRange | monacoNs.IPosition,
): void {
  const start = startOf(selectionOrPosition);
  if (!start) return;
  const uri = resource.toString();
  const answers = inBufferByModel.get(uri);
  const index = answers?.findIndex((a) => a.lineNumber === start.lineNumber && a.column === start.column) ?? -1;
  if (!answers || index < 0) return;
  const [match] = answers.splice(index, 1);
  if (answers.length === 0) inBufferByModel.delete(uri);
  trackNavFeature({ name: 'feature:navfeatures_goto_definition', props: { via: match.via } });
}

/** The opener body. Always `false` (see the header): Monaco keeps handling
 *  in-buffer jumps itself, and treats a `jamsql-nav:` open as "nothing to show
 *  in an editor" once its navigation ran. */
export function handleDefinitionOpen(
  resource: { readonly scheme?: string; toString(): string },
  selectionOrPosition?: monacoNs.IRange | monacoNs.IPosition,
): boolean {
  if (isDefinitionNavScheme(resource.scheme)) {
    const nav = lookupDefinitionNavTarget(resource.toString());
    if (nav && runDefinitionNavigation(nav)) {
      trackNavFeature({ name: 'feature:navfeatures_goto_definition', props: { via: nav.via } });
    }
    return false;
  }
  countInBufferDefinitionOpen(resource, selectionOrPosition);
  return false;
}

/** Perform the navigation. Returns whether anything happened (the OE node or
 *  connection can disappear between the probe and the open). Never throws:
 *  an exception here would surface as a Monaco error notification. */
export function runDefinitionNavigation(nav: DefinitionNavTarget): boolean {
  try {
    switch (nav.via) {
      case 'object-explorer':
        return navigateToObjectExplorer(nav.target).revealed;
      case 'column-target':
        return navigateToColumn(nav.target).opened;
      case 'routine-target':
        return navigateToRoutine(nav.target).opened;
      case 'routine-picker':
        useRoutinePickerStore.getState().openPicker(nav.candidates, nav.context);
        return true;
    }
  } catch (error) {
    console.error('[navigational-features] Go to Definition navigation failed:', error);
    return false;
  }
}

function startOf(
  target: monacoNs.IRange | monacoNs.IPosition | undefined,
): { lineNumber: number; column: number } | null {
  if (!target) return null;
  if ('startLineNumber' in target) {
    return { lineNumber: target.startLineNumber, column: target.startColumn };
  }
  return { lineNumber: target.lineNumber, column: target.column };
}
